// ============================================================
// EVIDENCE REQUIREMENTS RULES ENGINE
// ============================================================
// Configurable evidence requirements by job type, category, and workflow stage

// Evidence requirement levels
export const REQUIREMENT_LEVELS = {
  REQUIRED: 'required',      // Hard block - cannot proceed without
  RECOMMENDED: 'recommended', // Soft warning - can proceed but warned
  OPTIONAL: 'optional'       // No enforcement
};

// Evidence quality statuses
export const EVIDENCE_QUALITY = {
  COMPLETE: { label: 'Complete', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-100' },
  PARTIAL: { label: 'Partial', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-100' },
  MISSING_REQUIRED: { label: 'Missing Required', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-100' },
  AWAITING_REVIEW: { label: 'Awaiting Review', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-100' },
  REJECTED: { label: 'Rejected', color: 'bg-red-600', textColor: 'text-red-700', bgLight: 'bg-red-100' },
  APPROVED: { label: 'Approved', color: 'bg-emerald-600', textColor: 'text-emerald-700', bgLight: 'bg-emerald-100' }
};

// Workflow stages that require evidence checks
export const WORKFLOW_STAGES = {
  START_WORK: 'start_work',
  MARK_COMPLETE: 'mark_complete',
  SUBMIT_INVOICE: 'submit_invoice',
  CLOSE_JOB: 'close_job'
};

// ============================================================
// EVIDENCE REQUIREMENTS BY JOB TYPE
// ============================================================

export const EVIDENCE_RULES = {
  // Default rules for all job types
  default: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.RECOMMENDED,
      access_notes: REQUIREMENT_LEVELS.OPTIONAL
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.RECOMMENDED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.RECOMMENDED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.CLOSE_JOB]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // Electrical work
  electrical: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      certificate: REQUIREMENT_LEVELS.REQUIRED, // NICEIC/NAPIT cert
      sat_note: REQUIREMENT_LEVELS.RECOMMENDED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      certificate: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // Gas work
  gas: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      certificate: REQUIREMENT_LEVELS.REQUIRED, // Gas Safe cert mandatory
      sat_note: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      certificate: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // Plumbing/Drainage
  drainage: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      during: REQUIREMENT_LEVELS.RECOMMENDED, // During photos helpful for drainage
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.RECOMMENDED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // Roofing
  roofing: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      during: REQUIREMENT_LEVELS.REQUIRED, // During photos important for roof work
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      during: REQUIREMENT_LEVELS.RECOMMENDED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // General Repairs
  reactive_repair: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.RECOMMENDED
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.OPTIONAL
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      before: REQUIREMENT_LEVELS.REQUIRED,
      after: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  },
  
  // PPM (Planned Preventive Maintenance)
  ppm: {
    [WORKFLOW_STAGES.START_WORK]: {
      before: REQUIREMENT_LEVELS.OPTIONAL
    },
    [WORKFLOW_STAGES.MARK_COMPLETE]: {
      after: REQUIREMENT_LEVELS.REQUIRED,
      sat_note: REQUIREMENT_LEVELS.REQUIRED
    },
    [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
      after: REQUIREMENT_LEVELS.REQUIRED,
      invoice: REQUIREMENT_LEVELS.REQUIRED
    }
  }
};

// ============================================================
// EVIDENCE PROMPT MESSAGES
// ============================================================

export const EVIDENCE_PROMPTS = {
  [WORKFLOW_STAGES.START_WORK]: {
    title: 'Before Starting Work',
    subtitle: 'Document the current condition',
    prompts: [
      { category: 'before', label: 'Have before photos been uploaded?', description: 'Document pre-existing condition' },
      { category: 'access_notes', label: 'Any access issues noted?', description: 'Record access arrangements' }
    ]
  },
  [WORKFLOW_STAGES.MARK_COMPLETE]: {
    title: 'Before Marking Complete',
    subtitle: 'Verify completion evidence',
    prompts: [
      { category: 'before', label: 'Before photos uploaded', description: 'Pre-work condition documented' },
      { category: 'after', label: 'After photos uploaded', description: 'Completed work documented' },
      { category: 'sat_note', label: 'Satisfaction note', description: 'Customer signed off (if applicable)' },
      { category: 'certificate', label: 'Certificates uploaded', description: 'Required for Gas/Electrical' }
    ]
  },
  [WORKFLOW_STAGES.SUBMIT_INVOICE]: {
    title: 'Before Invoice Submission',
    subtitle: 'Verify all required evidence',
    prompts: [
      { category: 'after', label: 'After photos verified', description: 'Work completion documented' },
      { category: 'sat_note', label: 'Satisfaction note verified', description: 'Customer approval captured' },
      { category: 'certificate', label: 'Certificates verified', description: 'All certifications uploaded' },
      { category: 'invoice', label: 'Invoice document uploaded', description: 'Invoice ready for processing' }
    ]
  },
  [WORKFLOW_STAGES.CLOSE_JOB]: {
    title: 'Before Closing Job',
    subtitle: 'Final evidence check',
    prompts: [
      { category: 'before', label: 'Before photos complete', description: 'Initial state documented' },
      { category: 'after', label: 'After photos complete', description: 'Final state documented' }
    ]
  }
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get evidence rules for a specific job type
 * @param {string} jobType - The job type (electrical, gas, drainage, etc.)
 * @returns {object} Evidence rules for the job type
 */
export function getEvidenceRules(jobType) {
  const normalizedType = (jobType || '').toLowerCase().replace(/\s+/g, '_');
  return EVIDENCE_RULES[normalizedType] || EVIDENCE_RULES.default;
}

/**
 * Get requirements for a specific workflow stage
 * @param {string} jobType - The job type
 * @param {string} stage - The workflow stage
 * @returns {object} Requirements for the stage
 */
export function getStageRequirements(jobType, stage) {
  const rules = getEvidenceRules(jobType);
  return rules[stage] || EVIDENCE_RULES.default[stage] || {};
}

/**
 * Check evidence completeness against requirements
 * @param {Array} evidence - Array of evidence objects with category field
 * @param {string} jobType - The job type
 * @param {string} stage - The workflow stage to check
 * @returns {object} Completeness status
 */
export function checkEvidenceCompleteness(evidence, jobType, stage) {
  const requirements = getStageRequirements(jobType, stage);
  const presentCategories = new Set((evidence || []).map(e => e.category));
  
  const result = {
    canProceed: true,
    hardBlocks: [],
    softWarnings: [],
    status: EVIDENCE_QUALITY.COMPLETE,
    completedRequired: 0,
    totalRequired: 0,
    completedRecommended: 0,
    totalRecommended: 0
  };
  
  Object.entries(requirements).forEach(([category, level]) => {
    const hasEvidence = presentCategories.has(category);
    
    if (level === REQUIREMENT_LEVELS.REQUIRED) {
      result.totalRequired++;
      if (hasEvidence) {
        result.completedRequired++;
      } else {
        result.hardBlocks.push({
          category,
          message: `Required: ${getCategoryLabel(category)}`
        });
        result.canProceed = false;
      }
    } else if (level === REQUIREMENT_LEVELS.RECOMMENDED) {
      result.totalRecommended++;
      if (hasEvidence) {
        result.completedRecommended++;
      } else {
        result.softWarnings.push({
          category,
          message: `Recommended: ${getCategoryLabel(category)}`
        });
      }
    }
  });
  
  // Determine overall status
  if (result.hardBlocks.length > 0) {
    result.status = EVIDENCE_QUALITY.MISSING_REQUIRED;
  } else if (result.softWarnings.length > 0) {
    result.status = EVIDENCE_QUALITY.PARTIAL;
  } else {
    result.status = EVIDENCE_QUALITY.COMPLETE;
  }
  
  return result;
}

/**
 * Get human-readable label for evidence category
 */
export function getCategoryLabel(category) {
  const labels = {
    before: 'Before Photos',
    during: 'During Photos',
    after: 'After Photos',
    sat_note: 'Satisfaction Note',
    certificate: 'Certificate',
    invoice: 'Invoice',
    quote: 'Quote',
    access_notes: 'Access Notes',
    other: 'Other'
  };
  return labels[category] || category;
}

/**
 * Calculate evidence quality status for a job
 * @param {object} job - Job object with evidence_files and evidence_review
 * @returns {object} Quality status
 */
export function calculateEvidenceQuality(job) {
  const evidence = job?.evidence_files || [];
  const review = job?.evidence_review || {};
  
  // Check review status first
  if (review.status === 'approved') {
    return EVIDENCE_QUALITY.APPROVED;
  }
  if (review.status === 'rejected') {
    return EVIDENCE_QUALITY.REJECTED;
  }
  if (review.status === 'pending' && evidence.length > 0) {
    return EVIDENCE_QUALITY.AWAITING_REVIEW;
  }
  
  // Check completeness
  const jobType = job?.job_type || job?.category || 'default';
  const stage = getWorkflowStageFromStatus(job?.status);
  const completeness = checkEvidenceCompleteness(evidence, jobType, stage);
  
  return completeness.status;
}

/**
 * Map job status to workflow stage
 */
export function getWorkflowStageFromStatus(status) {
  const statusLower = (status || '').toLowerCase().replace(/[\s_-]+/g, '_');
  
  if (statusLower.includes('new') || statusLower.includes('assigned') || statusLower.includes('awaiting')) {
    return WORKFLOW_STAGES.START_WORK;
  }
  if (statusLower.includes('in_progress') || statusLower.includes('scheduled')) {
    return WORKFLOW_STAGES.MARK_COMPLETE;
  }
  if (statusLower.includes('completed') || statusLower.includes('review')) {
    return WORKFLOW_STAGES.SUBMIT_INVOICE;
  }
  if (statusLower.includes('invoiced') || statusLower.includes('paid')) {
    return WORKFLOW_STAGES.CLOSE_JOB;
  }
  
  return WORKFLOW_STAGES.START_WORK;
}

export default {
  REQUIREMENT_LEVELS,
  EVIDENCE_QUALITY,
  WORKFLOW_STAGES,
  EVIDENCE_RULES,
  EVIDENCE_PROMPTS,
  getEvidenceRules,
  getStageRequirements,
  checkEvidenceCompleteness,
  getCategoryLabel,
  calculateEvidenceQuality,
  getWorkflowStageFromStatus
};
